<template>
  <canvas ref="chartCanvas" />
</template>

<script setup>
import { LineController, LineElement, PointElement, CategoryScale, LinearScale, Chart } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

Chart.register(LineController, LineElement, PointElement, CategoryScale, LinearScale)

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  tmpKey: {
    type: String,
    default: 'maxt'
  }
})

const chartCanvas = ref(null)
let chart = null

watch(() => props.tmpKey, () => {
  if (!chart) {
    return
  }

  enableChartAnimation()

  chart.data.datasets[0].data = props.data.point.map((item) => item[props.tmpKey])
  chart.data.datasets[1].data = props.data.compare.map((item) => item[props.tmpKey])

  chart.update()
})

watch(() => props.data, () => {
  if (!chart) {
    return
  }

  enableChartAnimation()

  chart.data.datasets[1].data = props.data.compare.map((item) => item[props.tmpKey])
  chart.update()
})

const enableChartAnimation = () => {
  if (!chart) {
    return
  }

  chart.options.animation = {
    duration: 250,
    easing: 'easeOutQuad'
  }
}

const setup = () => {
  if (!chartCanvas.value) {
    return
  }

  const labels = props.data.point.map((item) => item.date)

  const data = {
    labels: labels,
    datasets: [
      {
        data: props.data.point.map((item) => item[props.tmpKey]),
        borderColor: '#1E9E08',
        fill: false
      },
      {
        data: props.data.compare.map((item) => item[props.tmpKey]),
        borderColor: '#ABCF4E',
        fill: false
      }
    ]
  }

  const options = {
    scales: {
      x: {
        offset: true,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        border: {
          display: false
        },
        ticks: {
          callback: (value) => {
            return value + '℃'
          },
          stepSize: 5
        },

        min: function () {
          const min = Math.min(...props.data.point.filter((item) => item?.mint != null).map((item) => item['mint']), ...props.data.compare.filter((item) => item?.mint != null).map((item) => item['mint']))
          return Math.floor(min / 5) * 5 - 5
        },
        max: function () {
          const max = Math.max(...props.data.point.filter((item) => item?.maxt != null).map((item) => item['maxt']), ...props.data.compare.filter((item) => item?.maxt != null).map((item) => item['maxt']))
          return Math.ceil(max / 5) * 5 + 5
        }
      }

    },
    animation: false,
    elements: {
      point: {
        pointStyle: 'circle',
        backgroundColor: function (context) {
          return context.dataset.borderColor
        },
        hoverRadius: 3
      }
    },
    responsive: true,
    aspectRatio: 2,
    plugins: {
      datalabels: {

        color: function (context) {
          return context.dataset.borderColor
        },
        textStrokeColor: 'rgba(255, 255, 255, 1)',
        textStrokeWidth: 2.5,
        font: {
          size: 16,
          weight: 'bold',
          lineHeight: 1
        },
        clamp: true,
        align: function () {
          return 'end'
        },
        display: true
      }
    }
  }

  chart = new Chart(chartCanvas.value, {
    plugins: [ChartDataLabels],
    type: 'line',
    data: data,
    options: options
  })
}

onMounted(() => {
  setup()
})
</script>
